// pages/WebsiteLinks/ListTrainedData.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import DataTable from 'react-data-table-component';
import { marked } from 'marked';
import EditTrainedData from "./EditTrainedData";
import EditSummary from "./EditSummary";

import {
    Modal as MuiModal,
    Box,
    Tooltip,
    Button,
    TextField,
    Checkbox,
    FormControlLabel,
    Menu,
    MenuItem,
    IconButton,
    ListItemIcon,
    ListItemText,
    useTheme,
    Card,
    CardContent,
    Grid,
    Typography,
    Alert,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    Chip
} from '@mui/material';

import {
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    Sync as SyncIcon,
    Summarize as SummaryIcon,
    MoreVert as MoreVertIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Warning as WarningIcon,
    Archive as ArchiveIcon
} from '@mui/icons-material';

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';

const delay = (ms) => new Promise(res => setTimeout(res, ms));

function SyncSettingsModal({ isOpen, onClose, onSave, document, isLoading, permission }) {
    const [isAutoSync, setIsAutoSync] = useState(document?.is_auto_sync ?? false);
    const [syncInterval, setSyncInterval] = useState(document?.sync_interval_days > 0 ? document?.sync_interval_days : document?.default_interval_days);

    useEffect(() => {
        setIsAutoSync(document?.is_auto_sync ?? false);
        const daysOfSyncInternal = document?.sync_interval_days > 0 ? document?.sync_interval_days : document?.default_interval_days;
        setSyncInterval(daysOfSyncInternal?.toString() ?? '');
    }, [document]);

    const handleSave = () => {
        const intervalVal = parseInt(syncInterval, 10);
        onSave({
            is_auto_sync: isAutoSync,
            sync_interval_days: (syncInterval === '' || isNaN(intervalVal) || intervalVal <= 0) ? null : intervalVal
        });
    };

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Sync Settings</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" sx={{ p: 1, backgroundColor: 'action.hover', borderRadius: 1, wordBreak: 'break-all', mb: 2 }}>
                    {document?.website_link}
                </Typography>
                <FormControlLabel
                    control={<Checkbox checked={isAutoSync} onChange={(e) => setIsAutoSync(e.target.checked)} />}
                    label="Enable Automatic Sync"
                    sx={{ mb: 1 }}
                />
                <TextField
                    label="Sync Interval (days)"
                    type="number"
                    fullWidth
                    value={syncInterval}
                    onChange={(e) => setSyncInterval(e.target.value)}
                    disabled={!isAutoSync}
                    InputProps={{ inputProps: { min: 1 } }}
                    helperText={`Leave blank or 0 to use global default (${document?.default_interval_days} days).`}
                    variant="outlined"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                {permission.canModify && (
                    <Button onClick={handleSave} variant="contained" disabled={isLoading} startIcon={isLoading ? <CircularProgress size={20} /> : null}>
                        Save Settings
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

function PendingUpdateModal({ isOpen, onClose, onAccept, onReject, originalContent, pendingContent, documentUrl, isLoading, permission }) {
    const commonExtensions = [StarterKit, Link.configure({ openOnClick: false }), Underline, Table.configure({ resizable: true }), TableCell, TableHeader, TableRow];
    const originalEditor = useEditor({ extensions: commonExtensions, content: originalContent, editable: false });
    const pendingEditor = useEditor({ extensions: commonExtensions, content: pendingContent, editable: permission.canModify });

    useEffect(() => { originalEditor?.commands.setContent(originalContent || ""); }, [originalContent, originalEditor]);
    useEffect(() => { pendingEditor?.commands.setContent(pendingContent || ""); }, [pendingContent, pendingEditor]);

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { height: '90vh' } }}>
            <DialogTitle>Review Pending Update</DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" sx={{ p: 1, backgroundColor: 'action.hover', borderRadius: 1, wordBreak: 'break-all' }}>
                    {documentUrl}
                </Typography>
                <Grid container spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
                    <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>Current Version</Typography>
                        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, flexGrow: 1, overflow: 'auto', p: 1 }}>
                            <EditorContent editor={originalEditor} />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" color="primary" sx={{ mb: 1 }}>New Version (Pending)</Typography>
                        <Box sx={{ border: 1, borderColor: 'primary.main', borderRadius: 1, flexGrow: 1, overflow: 'auto', p: 1 }}>
                            <EditorContent editor={pendingEditor} />
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            {permission.canModify && (
                <DialogActions>
                    <Button onClick={() => onReject(originalEditor.getHTML())} color="error" disabled={isLoading} startIcon={isLoading === 'reject' ? <CircularProgress size={20} /> : null}>
                        Reject
                    </Button>
                    <Button onClick={() => onAccept(pendingEditor.getHTML())} variant="contained" disabled={isLoading} startIcon={isLoading === 'accept' ? <CircularProgress size={20} /> : null}>
                        Approve & Update
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
}

const BulkStatusModal = ({ isOpen, onRequestClose, results, title }) => (
    <Dialog open={isOpen} onClose={onRequestClose} fullWidth maxWidth="md">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
            <List>
                {results.map((result, index) => (
                    <ListItem key={index} divider>
                        <ListItemIcon>
                            {result.status === 'success' && <CheckCircleIcon color="success" />}
                            {result.status === 'error' && <ErrorIcon color="error" />}
                            {result.status === 'pending' && <HourglassEmptyIcon color="action" />}
                        </ListItemIcon>
                        <ListItemText primary={result.website_link} secondary={result.message} primaryTypographyProps={{ style: { wordBreak: 'break-all' } }} />
                    </ListItem>
                ))}
            </List>
        </DialogContent>
        <DialogActions><Button onClick={onRequestClose}>Close</Button></DialogActions>
    </Dialog>
);

export default function ListTrainedData({ refresh, setRefresh, chatbotId, permission }) {
    const theme = useTheme();
    const [documents, setDocuments] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEditSummaryModalOpen, setIsEditSummaryModalOpen] = useState(false);
    const [isSyncSettingsModalOpen, setIsSyncSettingsModalOpen] = useState(false);
    const [isPendingUpdateModalOpen, setIsPendingUpdateModalOpen] = useState(false);
    const [currentDocument, setCurrentDocument] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [isBulkArchiving, setIsBulkArchiving] = useState(false);
    const [isBulkRefreshing, setIsBulkRefreshing] = useState(false);
    const [isBulkSummaryRefreshing, setIsBulkSummaryRefreshing] = useState(false);
    const [bulkRefreshResults, setBulkRefreshResults] = useState([]);
    const [isBulkRefreshModalOpen, setIsBulkRefreshModalOpen] = useState(false);
    const [bulkSummaryRefreshResults, setBulkSummaryRefreshResults] = useState([]);
    const [isBulkSummaryRefreshModalOpen, setIsBulkSummaryRefreshModalOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectionLimit, setSelectionLimit] = useState(50);
    const [userPlan, setUserPlan] = useState('freemium');

    useEffect(() => {
        const fetchUserPlan = async () => {
            try {
                const response = await axiosInstance.get(`/subscriptions/current`);
                const planName = response.data?.plan?.name?.toLowerCase();
                setUserPlan(planName);
                if (planName === 'basic') setSelectionLimit(200);
                else if (planName === 'premium') setSelectionLimit(Number.MAX_SAFE_INTEGER);
                else setSelectionLimit(50);
            } catch (error) {
                console.error("Error fetching user subscription plan, defaulting to Freemium limit.", error);
                setSelectionLimit(50);
            }
        };
        fetchUserPlan();
    }, []);

    const fetchDocuments = useCallback(async (page = 1, limit = perPage, search = "") => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit, search, ...(chatbotId && { chatbot_id: chatbotId }) };
            const response = await axiosInstance.get(`/get-trained-website-links`, { params });
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
            setDocuments(responseData.data || []);
            setTotalRows(responseData.total || 0);
            setCurrentPage(page);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch trained data.');
        } finally {
            setLoading(false);
        }
    }, [perPage, chatbotId]);

    useEffect(() => {
        document.title = 'Trained Data | KlonIT';
        fetchDocuments(1, perPage, searchText);
    }, [refresh, searchText]);

    const getUpgradeMessage = (plan) => {
        if (plan === 'freemium') return "Upgrade to Basic to select up to 200, or Premium to select all.";
        if (plan === 'basic') return "Upgrade to Premium to select all.";
        return "There may be a system or browser limitation.";
    };
    
    const handleRowSelected = (state) => {
        if (state.selectedRows.length > selectionLimit) {
            setError(`Your selection limit is ${selectionLimit} items. ${getUpgradeMessage(userPlan)}`);
            const limitedSelection = state.selectedRows.slice(0, selectionLimit);
            setSelectedRows(limitedSelection);
            setToggleCleared(!toggleCleared);
        } else {
            setSelectedRows(state.selectedRows);
            setError(null);
        }
    };
    
    const handleOpenMenu = (event, row) => {
        setMenuAnchorEl(event.currentTarget);
        setCurrentDocument(row);
    };

    const handleCloseMenu = () => {
        setMenuAnchorEl(null);
    };
    
    const handleDelete = async (documentId) => {
        if (!window.confirm(`Delete item ID ${documentId}? This cannot be undone.`)) return;
        try {
            setError(null);
            const params = chatbotId ? { chatbot_id: chatbotId } : {};
            await axiosInstance.delete(`/delete-website-scrape-data/${documentId}`, { params });
            setError("Item deleted successfully.");
            fetchDocuments(currentPage, perPage, searchText);
        } catch (err) {
            setError(`Error deleting item: ${err.response?.data?.message || err.message}`);
        }
    };

    const handleArchive = async (documentId) => {
        if (!window.confirm(`Archive item ID ${documentId}?`)) return;
        setError(null);
        try {
            const params = chatbotId ? { chatbot_id: chatbotId } : {};
            await axiosInstance.post(`/archive-website-data`, { link_ids: [documentId] }, { params });
            setError("Item archived successfully.");
            fetchDocuments(currentPage, perPage, searchText);
            if (setRefresh) setRefresh(prev => !prev);
        } catch (err) {
            setError(`Error archiving item: ${err.response?.data?.message || err.message}`);
        }
    };

    const handleBulkOperation = async (operation) => {
        const selectedIds = selectedRows.map(row => row.id);
        if (selectedIds.length === 0) return;
    
        const operationMap = {
            delete: {
                confirm: `Delete ${selectedIds.length} selected item(s)? This cannot be undone.`,
                setter: setIsBulkDeleting,
                endpoint: (id) => axiosInstance.delete(`/delete-website-scrape-data/${id}`, { params: { chatbot_id: chatbotId } }),
                successMsg: 'items deleted successfully.',
                errorMsg: 'Error during bulk delete'
            },
            archive: {
                confirm: `Archive ${selectedIds.length} selected item(s)?`,
                setter: setIsBulkArchiving,
                endpoint: () => axiosInstance.post(`/archive-website-data`, { link_ids: selectedIds }, { params: { chatbot_id: chatbotId } }),
                successMsg: 'items archived successfully.',
                errorMsg: 'Error during bulk archive'
            }
        };
    
        const config = operationMap[operation];
        if (!config || !window.confirm(config.confirm)) return;
    
        config.setter(true);
        setError(null);
    
        try {
            if (operation === 'delete') {
                let successCount = 0;
                for (const id of selectedIds) {
                    try { await config.endpoint(id); successCount++; } catch (e) { console.error(e); }
                }
                setError(`${successCount} ${config.successMsg}`);
            } else {
                const response = await config.endpoint();
                setError(response.data.message || `${selectedIds.length} ${config.successMsg}`);
            }
        } catch (err) {
            setError(`${config.errorMsg}: ${err.response?.data?.message || err.message}`);
        } finally {
            setSelectedRows([]);
            fetchDocuments(currentPage, perPage, searchText);
            if (operation === 'archive' && setRefresh) setRefresh(prev => !prev);
            config.setter(false);
        }
    };

    const handleBulkRefresh = async (isSummary = false) => {
        if (selectedRows.length === 0) return;
        const setter = isSummary ? setIsBulkSummaryRefreshing : setIsBulkRefreshing;
        const resultsSetter = isSummary ? setBulkSummaryRefreshResults : setBulkRefreshResults;
        const modalSetter = isSummary ? setIsBulkSummaryRefreshModalOpen : setIsBulkRefreshModalOpen;
        const endpoint = isSummary ? (id) => axiosInstance.post(`/refresh-summary/${id}`, {}, { params: { chatbot_id: chatbotId } }) : (id, link) => axiosInstance.post(`/refresh-website-data`, { data: [[id], [link]] }, { params: { chatbot_id: chatbotId } });
        
        setter(true);
        setError(null);
        const initialResults = selectedRows.map(doc => ({ website_link: doc.website_link, status: 'pending', message: null }));
        resultsSetter(initialResults);
        modalSetter(true);

        let currentResults = [...initialResults];
        for (let i = 0; i < selectedRows.length; i++) {
            const doc = selectedRows[i];
            try {
                const response = isSummary ? await endpoint(doc.id) : await endpoint(doc.id, doc.website_link);
                currentResults[i] = { ...currentResults[i], status: 'success', message: response.data?.message || "Refresh completed." };
            } catch (err) {
                currentResults[i] = { ...currentResults[i], status: 'error', message: err.response?.data?.message || err.message };
            }
            resultsSetter([...currentResults]);
            if (i < selectedRows.length - 1) await delay(1000);
        }
        setter(false);
    };

    const handleSingleRefresh = async (document, isSummary = false) => {
        setIsProcessing(true);
        setError(null);
        try {
            const params = chatbotId ? { chatbot_id: chatbotId } : {};
            const response = isSummary 
                ? await axiosInstance.post(`/refresh-summary/${document.id}`, {}, { params })
                : await axiosInstance.post(`/refresh-website-data`, { data: [[document.id], [document.website_link]] }, { params });
            setError(response.data.message || "Operation completed successfully.");
            fetchDocuments(currentPage, perPage, searchText);
        } catch (err) {
            setError(`Error: ${err.response?.data?.message || 'Operation failed.'}`);
        } finally {
            setIsProcessing(false);
        }
    };
    
    const handleSaveSyncSettings = async (settings) => {
        if (!currentDocument) return;
        setIsProcessing(true);
        setError(null);
        try {
            const payload = { updates: [{ id: currentDocument.id, ...settings }] };
            const params = chatbotId ? { chatbot_id: chatbotId } : {};
            await axiosInstance.post(`/update_website_link_sync_settings`, payload, { params });
            setError("Sync settings updated successfully.");
            fetchDocuments(currentPage, perPage, searchText);
            setIsSyncSettingsModalOpen(false);
        } catch (err) {
            setError(`Failed to update sync settings: ${err.response?.data?.message || err.message}`);
        } finally {
            setIsProcessing(false);
        }
    };
    
    const handleResolvePendingUpdate = async (action, pageData) => {
        if (!currentDocument) return;
        setIsProcessing(action);
        setError(null);
        try {
            const params = chatbotId ? { chatbot_id: chatbotId } : {};
            const payload = { id: currentDocument.id, page_content: pageData, all_data: currentDocument };
            await axiosInstance.post(`/update-trained-data`, payload, { params });
            setError(`Pending update ${action === 'accept' ? 'accepted' : 'rejected'} successfully.`);
            fetchDocuments(currentPage, perPage, searchText);
            setIsPendingUpdateModalOpen(false);
        } catch (err) {
            setError(`Failed to ${action} pending update: ${err.response?.data?.message || err.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const customDataTableStyles = {
        table: { style: { backgroundColor: theme.palette.background.paper, } },
        headRow: { style: { backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9", borderBottom: `1px solid ${theme.palette.divider}`, }, },
        headCells: { style: { fontWeight: 600, color: theme.palette.text.primary, } },
        rows: { style: { backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, '&:hover': { backgroundColor: `${theme.palette.action.hover} !important` } } },
        pagination: { style: { backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, borderTop: `1px solid ${theme.palette.divider}` } },
        expanderRow: { style: { backgroundColor: theme.palette.action.hover } },
    };

    const columns = [
        { name: 'URL', selector: row => row.website_link, sortable: true, grow: 2, cell: row => (<Tooltip title={row.website_link}><Typography noWrap sx={{ maxWidth: { xs: '150px', sm: '300px' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{row.website_link}</Typography></Tooltip>) },
        { name: 'Page Name', selector: row => row.title_tag, sortable: true, hide: 'sm', cell: row => (<Tooltip title={row.title_tag}><Typography noWrap sx={{ maxWidth: '300px' }}>{row.title_tag || '(No Title)'}</Typography></Tooltip>) },
        { name: 'Auto Sync', selector: row => row.is_auto_sync, sortable: true, center: true, hide: 'md', cell: row => (<Tooltip title={`Auto-sync is ${row.is_auto_sync ? 'ON' : 'OFF'}. Click to change.`}><Checkbox size="small" checked={row.is_auto_sync} onClick={() => { setCurrentDocument(row); setIsSyncSettingsModalOpen(true); }} /></Tooltip>) },
        { name: 'Last Synced', selector: row => row.last_sync_check_date, sortable: true, hide: 'md', format: row => row.last_sync_check_date ? new Date(row.last_sync_check_date).toLocaleString() : 'Never' },
        { name: 'Status', center: true, width: '80px', cell: row => (row.has_pending_update ? (<Tooltip title="Content has an external update. Review changes."><Chip icon={<WarningIcon />} label="Pending" color="warning" size="small" onClick={() => { setCurrentDocument(row); setIsPendingUpdateModalOpen(true); }} /></Tooltip>) : (<Tooltip title={row.is_trained ? "Data is trained" : "Data not trained / training pending"}><Checkbox size="small" icon={<HourglassEmptyIcon />} checkedIcon={<CheckCircleIcon />} checked={row.is_trained} disabled /></Tooltip>)) },
        { name: '', width: '50px', cell: (row) => (<IconButton size="small" aria-label="actions" onClick={(e) => handleOpenMenu(e, row)}><MoreVertIcon fontSize="small" /></IconButton>), ignoreRowClick: true, },
    ];

    const ExpandedRow = ({ data }) => (
        <Box sx={{ p: 2, backgroundColor: 'action.hover' }}>
            <Typography variant="body2" gutterBottom><strong>URL ID:</strong> {data.id}</Typography>
            <Typography variant="body2" gutterBottom><strong>Title Tag:</strong> {data.title_tag || 'N/A'}</Typography>
            <Box sx={{ my: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Summary:</Typography>
                <Card variant="outlined"><CardContent sx={{ maxHeight: 200, overflow: 'auto' }}><Box dangerouslySetInnerHTML={{ __html: marked.parse(data.summary || 'N/A') }} /></CardContent></Card>
            </Box>
        </Box>
    );
    
    const modalStyle = {
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '80%', md: 500 }, maxHeight: '90vh', overflowY: 'auto', bgcolor: 'background.paper', boxShadow: 24, p: { xs: 2, sm: 3 }, borderRadius: '8px', outline: 'none'
    };


    return (
        <Box>
            <Card>
                <CardContent>
                    <Grid container spacing={1} sx={{ mb: 1.5 }} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                                {permission.canDelete && <Button variant="outlined" color="error" onClick={() => handleBulkOperation('delete')} disabled={selectedRows.length === 0 || isBulkDeleting} startIcon={isBulkDeleting ? <CircularProgress size={16} /> : <DeleteIcon />} size="small" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Delete ({selectedRows.length})</Button>}
                                {permission.canModify && <Button variant="outlined" color="primary" onClick={() => handleBulkRefresh(false)} disabled={selectedRows.length === 0 || isBulkRefreshing} startIcon={isBulkRefreshing ? <CircularProgress size={16} /> : <SyncIcon />} size="small" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Refresh ({selectedRows.length})</Button>}
                                {permission.canModify && <Button variant="outlined" color="secondary" onClick={() => handleBulkRefresh(true)} disabled={selectedRows.length === 0 || isBulkSummaryRefreshing} startIcon={isBulkSummaryRefreshing ? <CircularProgress size={16} /> : <SummaryIcon />} size="small" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Summary ({selectedRows.length})</Button>}
                                {permission.canModify && <Button variant="outlined" color="inherit" onClick={() => handleBulkOperation('archive')} disabled={selectedRows.length === 0 || isBulkArchiving} startIcon={isBulkArchiving ? <CircularProgress size={16} /> : <ArchiveIcon />} size="small" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>Archive ({selectedRows.length})</Button>}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth variant="outlined" size="small" placeholder="Search URL, Title..." value={searchText} onChange={(e) => setSearchText(e.target.value)} /></Grid>
                    </Grid>

                    {error && <Alert severity={error.includes("success") ? "success" : "warning"} onClose={() => setError(null)} sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}

                    {loading ? (
                        <Box display="flex" flexDirection="column" alignItems="center" py={4}><CircularProgress /><Typography mt={2}>Loading Trained Data...</Typography></Box>
                    ) : documents.length === 0 ? (
                        <Box textAlign="center" py={4}><Typography variant="h6" color="text.secondary">No trained website data found.</Typography><Typography color="text.secondary">Add links and scrape them to see data here.</Typography></Box>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={documents}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationPerPage={perPage}
                            onChangePage={(page) => fetchDocuments(page, perPage, searchText)}
                            onChangeRowsPerPage={(newPerPage, page) => { setPerPage(newPerPage); fetchDocuments(page, newPerPage, searchText); }}
                            selectableRows
                            onSelectedRowsChange={handleRowSelected}
                            clearSelectedRows={toggleCleared}
                            expandableRows
                            expandableRowsComponent={ExpandedRow}
                            customStyles={customDataTableStyles}
                            theme={theme.palette.mode === 'dark' ? 'dark' : 'default'}
                        />
                    )}
                </CardContent>
            </Card>

            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
                <MenuItem onClick={() => { window.open(currentDocument?.website_link, '_blank'); handleCloseMenu(); }}><ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon><ListItemText>View Website</ListItemText></MenuItem>
                {permission.canModify && [
                    <MenuItem key="refresh" disabled={isProcessing} onClick={() => { handleSingleRefresh(currentDocument, false); handleCloseMenu(); }}><ListItemIcon><SyncIcon fontSize="small" /></ListItemIcon><ListItemText>Refresh & Retrain</ListItemText></MenuItem>,
                    <MenuItem key="summary" disabled={isProcessing} onClick={() => { handleSingleRefresh(currentDocument, true); handleCloseMenu(); }}><ListItemIcon><SummaryIcon fontSize="small" /></ListItemIcon><ListItemText>Summary Refresh</ListItemText></MenuItem>,
                    <MenuItem key="edit" onClick={() => { setIsEditModalOpen(true); handleCloseMenu(); }}><ListItemIcon><EditIcon fontSize="small" /></ListItemIcon><ListItemText>Edit Content</ListItemText></MenuItem>,
                    <MenuItem key="edit-summary" onClick={() => { setIsEditSummaryModalOpen(true); handleCloseMenu(); }}><ListItemIcon><EditIcon fontSize="small" /></ListItemIcon><ListItemText>Edit Summary</ListItemText></MenuItem>,
                    <MenuItem key="archive" onClick={() => { handleArchive(currentDocument.id); handleCloseMenu(); }}><ListItemIcon><ArchiveIcon fontSize="small" /></ListItemIcon><ListItemText>Archive</ListItemText></MenuItem>
                ]}
                {permission.canDelete && <MenuItem onClick={() => { handleDelete(currentDocument.id); handleCloseMenu(); }} sx={{ color: 'error.main' }}><ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon><ListItemText>Delete</ListItemText></MenuItem>}
            </Menu>

            <MuiModal open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}><Box sx={{...modalStyle, width: { xs: '95%', sm: '90%', md: '80vw' }, maxWidth: '1400px'}}><EditTrainedData document={currentDocument} onClose={() => setIsEditModalOpen(false)} onUpdate={() => { fetchDocuments(currentPage, perPage, searchText); setIsEditModalOpen(false); }} chatbotId={chatbotId} permission={permission} /></Box></MuiModal>
            <MuiModal open={isEditSummaryModalOpen} onClose={() => setIsEditSummaryModalOpen(false)}><Box sx={modalStyle}><EditSummary document={currentDocument} onClose={() => setIsEditSummaryModalOpen(false)} onUpdate={() => { fetchDocuments(currentPage, perPage, searchText); setIsEditSummaryModalOpen(false); }} chatbotId={chatbotId} permission={permission} /></Box></MuiModal>
            
            {currentDocument && <SyncSettingsModal isOpen={isSyncSettingsModalOpen} onClose={() => setIsSyncSettingsModalOpen(false)} onSave={handleSaveSyncSettings} document={currentDocument} isLoading={isProcessing} permission={permission} />}
            {currentDocument && <PendingUpdateModal isOpen={isPendingUpdateModalOpen} onClose={() => setIsPendingUpdateModalOpen(false)} onAccept={(data) => handleResolvePendingUpdate('accept', data)} onReject={(data) => handleResolvePendingUpdate('reject', data)} originalContent={currentDocument.page_content} pendingContent={currentDocument.pending_update_content} documentUrl={currentDocument.website_link} isLoading={isProcessing} permission={permission} />}
            
            <BulkStatusModal isOpen={isBulkRefreshModalOpen} onRequestClose={() => setIsBulkRefreshModalOpen(false)} results={bulkRefreshResults} title="Bulk Refresh Status" />
            <BulkStatusModal isOpen={isBulkSummaryRefreshModalOpen} onRequestClose={() => setIsBulkSummaryRefreshModalOpen(false)} results={bulkSummaryRefreshResults} title="Bulk Summary Refresh Status" />
        </Box>
    );
}